import _ from 'lodash';
import { TextBlock } from 'pages/approvals/modals/ApprovalItems/blocks/TextBlock';
import { BlockLayoutFromCsv } from 'pages/documents_v2/views/edit_area/blocks/block_types/BlockLayoutFromCsv';
import { EmptyTextBlock } from 'pages/documents_v2/views/edit_area/blocks/block_types/EmptyTextBlock';
import { LayoutBlockPagination } from 'pages/documents_v2/views/edit_area/blocks/block_types/layout_block/LayoutBlockPagination';
import { filterAvailableBlocks, getBlockData, parseBlockContent } from 'pages/global/BlockLayoutHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';

const ITEMS_PER_PAGE = 20;
const emptyArray = [];

export function BlockLayoutBlock({ block, areaBlocks = [], baseBlocks = [], hideChanges }) {
    const blockData = getBlockData(block);

    if (blockData.readOnly === true) {
        return (
            <div className="py-4">
                <BlockLayoutFromCsv block={block} blockData={blockData} />
            </div>
        );
    }

    return (
        <div className="p-4">
            <NormalBlockLayout
                block={block}
                itemsPerPage={ITEMS_PER_PAGE}
                areaBlocks={areaBlocks}
                baseBlocks={baseBlocks}
                hideChanges={hideChanges}
            />
        </div>
    );
}

function NormalBlockLayout({ block, itemsPerPage = 20, areaBlocks = [], baseBlocks, hideChanges }) {
    const [itemOffset, setItemOffset] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSearchBlock, setSelectedSearchBlock] = useState(undefined);

    const blockContentAsString = JSON.stringify(getBlockContent());

    const blockContent = useMemo(() => {
        return JSON.parse(blockContentAsString);
    }, [blockContentAsString]);

    const rows = blockContent?.data ?? emptyArray;
    const pageCount = Math.ceil(rows.length / itemsPerPage);
    const columnsPerRow = rows.length > 0 ? rows[0].length : 2;

    useEffect(() => {
        // When removing rows, move to the previous page if the offset is bigger than the number of rows
        if (itemOffset >= rows.length) {
            setItemOffset(Math.max(itemOffset - itemsPerPage, 0));
        }
    }, [itemOffset, rows, itemsPerPage]);

    const handlePageClick = useCallback(
        (event) => {
            const newOffset = event.selected * itemsPerPage;
            setItemOffset(newOffset);
        },
        [itemsPerPage],
    );

    const currentItems = useMemo(() => {
        const newPageCount = Math.ceil(rows.length / itemsPerPage);
        let tempOffset = itemOffset;

        if (newPageCount < pageCount) {
            tempOffset = 0;
        }

        const endOffset = tempOffset + itemsPerPage;
        return rows.slice(tempOffset, endOffset);
    }, [rows, itemsPerPage, itemOffset, pageCount]);

    return (
        <>
            {rows.length >= itemsPerPage && (
                <div className="pt-4">
                    <LayoutBlockPagination
                        currentItemsLength={currentItems.length}
                        handlePageClick={handlePageClick}
                        itemOffset={itemOffset}
                        pageCount={pageCount}
                        totalItemsLength={rows.length}
                        itemsPerPage={itemsPerPage}
                        columnsPerRow={columnsPerRow}
                        setItemOffset={setItemOffset}
                        rows={rows}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        setSelectedSearchBlock={setSelectedSearchBlock}
                        readOnly={true}
                        areaBlocks={areaBlocks}
                    />
                </div>
            )}

            <BlockLayout
                currentItems={currentItems}
                block={block}
                itemOffset={itemOffset}
                areaBlocks={areaBlocks}
                baseBlocks={baseBlocks}
                hideChanges={hideChanges}
            />
        </>
    );

    function getBlockContent() {
        return filterAvailableBlocks(parseBlockContent(block.latestContent || block.baseContent), areaBlocks);
    }
}

function BlockLayout({ currentItems, block, itemOffset, areaBlocks = [], baseBlocks, hideChanges }) {
    return (
        <RenderBlockLayout
            block={block}
            areaBlocks={areaBlocks}
            baseBlocks={baseBlocks}
            blockLayout={currentItems}
            itemOffset={itemOffset}
            hideChanges={hideChanges}
        />
    );
}

function RenderBlockLayout({ block, areaBlocks, baseBlocks, blockLayout = [], itemOffset, hideChanges }) {
    const { properties } = block;

    const showTableHeader = properties?.showTableHeader ?? true;
    const highlightFirstColumn = properties?.highlightFirstColumn ?? false;

    const columnWidth =
        blockLayout[0] && blockLayout[0].length > 0 ? Math.round(100 / blockLayout[0].length) + '%' : '100%';

    return (
        <div className="table-responsive">
            <Table bordered className="mb-2">
                <tbody>
                    {blockLayout.map((el, ind) => (
                        <tr key={block.key + '-row-' + ind}>
                            {el.map((col, colIndex) => {
                                const isTableHeader =
                                    (showTableHeader && ind === 0 && itemOffset === 0) ||
                                    (highlightFirstColumn && colIndex === 0);

                                return (
                                    <td
                                        width={columnWidth}
                                        style={getListStyle(false, isTableHeader)}
                                        key={block.key + '-col-' + colIndex}
                                    >
                                        <div>
                                            {col.map((item, index) => (
                                                <LayoutItem
                                                    areaBlock={areaBlocks.find((_block) => _block.id === item.id)}
                                                    baseBlock={baseBlocks.find((_block) => _block.id === item.id)}
                                                    hideChanges={hideChanges}
                                                    key={`layout-${item.id}-${index}`}
                                                />
                                            ))}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

function LayoutItem({ areaBlock, baseBlock, hideChanges }) {
    if (!areaBlock) {
        return null;
    }

    const areaBlockContent = areaBlock.latestContent || areaBlock.baseContent || '';
    const blockContent = !hideChanges && baseBlock ? baseBlock.renderedDiffContent : areaBlockContent;

    if (_.isEmpty(blockContent)) {
        return <EmptyTextBlock />;
    }

    return (
        <div className="position-relative">
            <div className="edit-area-block edit-area-block-type-text d-flex align-items-start read-only">
                <div className="edit-area-block-content">
                    <TextBlock content={blockContent} />
                </div>
            </div>
        </div>
    );
}

const getListStyle = (isDraggingOver, isTableHeader, isDragging = false) => ({
    background: isDraggingOver ? '#00e67614' : isDragging ? undefined : isTableHeader ? '#f6f8fa' : undefined,
    height: 87,
    minWidth: 140,
    overflow: isDragging ? 'hidden' : undefined,
});
